import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Crown, Sparkles, AlertTriangle, Clock, CheckCircle2, Calendar as CalendarIcon, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import api from "@/lib/api";
import useRazorpayCheckout from "@/lib/useRazorpayCheckout";
import { useAuth } from "@/lib/auth";
import PaymentSummaryDialog from "@/components/payments/PaymentSummaryDialog";

/**
 * SubscriptionStatusCard — the visual centrepiece of the owner dashboard for
 * paid-plan (Featured / Premium) owners. Displays plan, start date, expiry,
 * days-remaining and a color-coded status badge, plus a prominent Renew CTA.
 *
 * States rendered:
 *   * active         → green tick, calm palette
 *   * expiring_soon  → amber, urgency copy
 *   * expired        → red, blocking banner tone
 *   * none / null    → renders nothing (free owners don't need this card)
 *
 * We deliberately keep the network fetch inside this component (rather than
 * hoisting to the dashboard) so it can be dropped into any owner screen — the
 * banner above the Listing form, the top of the analytics view, wherever.
 * The cost is a small extra request; the benefit is self-contained UX.
 */
const STATUS_META = {
  active:         { icon: CheckCircle2, label: "Active",         chipBg: "bg-[#DCFCE7]", chipText: "text-[#065F46]", ring: "ring-[#86EFAC]" },
  expiring_soon:  { icon: Clock,        label: "Expiring Soon",  chipBg: "bg-[#FEF3C7]", chipText: "text-[#92400E]", ring: "ring-[#FCD34D]" },
  expired:        { icon: AlertTriangle,label: "Expired",        chipBg: "bg-[#FEE2E2]", chipText: "text-[#991B1B]", ring: "ring-[#FCA5A5]" },
};

function fmtDate(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    return d.toLocaleDateString("en-IN", { day: "numeric", month: "long", year: "numeric" });
  } catch {
    return "—";
  }
}

export default function SubscriptionStatusCard({ compact = false }) {
  const [sub, setSub] = useState(null);
  const [spa, setSpa] = useState(null);
  const [loading, setLoading] = useState(true);
  const { user } = useAuth();

  const load = () => {
    api.get("/owner/subscription")
      .then((r) => { setSub(r.data.subscription); setSpa(r.data.spa); })
      .catch(() => {})
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    let cancelled = false;
    api.get("/owner/subscription")
      .then((r) => { if (!cancelled) { setSub(r.data.subscription); setSpa(r.data.spa); } })
      .catch(() => {})
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, []);

  // Razorpay checkout hook — reloads the subscription card on successful
  // verification so the new expiry / status appears without a page refresh.
  const { open: openCheckout, loading: paying, config: rzpConfig } = useRazorpayCheckout({
    onSuccess: () => load(),
  });

  // Payment summary dialog state — same two-step pattern as the Plans page.
  const [showSummary, setShowSummary] = useState(false);
  const summaryPricing = sub?.plan && rzpConfig?.plans?.[sub.plan]
    ? rzpConfig.plans[sub.plan]
    : null;

  if (loading || !sub) return null;
  if (!sub.is_paid) return null;   // Free plan → no card

  const meta = STATUS_META[sub.status] || STATUS_META.active;
  const Icon = meta.icon;
  const isPremium = sub.plan === "premium";
  const daysLeft = sub.days_remaining;
  const isExpired = sub.status === "expired";
  const daysDisplay = daysLeft == null ? "—" : Math.abs(daysLeft);
  const dayLabel = daysLeft == null ? "" : (Math.abs(daysLeft) === 1 ? "day" : "days");

  return (
    <div
      data-testid="subscription-status-card"
      className={`rounded-2xl border p-5 sm:p-6 ring-1 ${meta.ring} ${isExpired ? "bg-[#FEF2F2] border-[#FCA5A5]" : "bg-white border-[#E7DDCF]"}`}
    >
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <div className="text-xs uppercase tracking-widest text-[#7A5A22]">Current Plan</div>
            <span
              data-testid="subscription-plan-chip"
              className={`inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-xs font-medium ${isPremium ? "bg-[#F5EFE6] text-[#7A5A22] border border-[#C9A86A]" : "bg-[#EEF2FF] text-[#1E3A8A] border border-[#93C5FD]"}`}
            >
              {isPremium ? <Crown size={12} /> : <Sparkles size={12} />}
              {isPremium ? "Premium" : "Featured"}
            </span>
            <span
              data-testid="subscription-status-chip"
              className={`inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-xs font-semibold ${meta.chipBg} ${meta.chipText}`}
            >
              <Icon size={12} /> {meta.label}
            </span>
          </div>
          <h2 className="font-heading text-2xl sm:text-3xl text-[#111827] mt-2 leading-tight">
            {isExpired ? "Your subscription has expired" : (
              <>
                {daysLeft > 0
                  ? <>Renews in <span data-testid="days-remaining">{daysDisplay}</span> {dayLabel}</>
                  : "Expires today"}
              </>
            )}
          </h2>
          {!compact && (
            <p className="text-sm text-[#4B5563] mt-1">
              {isExpired
                ? "Renew now to restore your Featured/Premium benefits."
                : (sub.status === "expiring_soon"
                    ? "Renew soon to avoid any disruption to your listing benefits."
                    : "Your paid plan is active. All premium benefits are enabled.")}
            </p>
          )}
        </div>

        <Button
          onClick={() => {
            // Business rule: paid-plan owners can only renew after their
            // current cycle ends. Mid-cycle renew would silently double-charge
            // and reset the 30-day clock. Server also enforces this via 409.
            if (sub.status === "active" || sub.status === "expiring_soon") {
              // Send them to /plans where the "Locked until…" copy explains why.
              window.location.href = "/plans";
              return;
            }
            // Open the payment summary dialog (subtotal + GST + total) before
            // handing off to the Razorpay checkout modal.
            setShowSummary(true);
          }}
          disabled={paying}
          data-testid="subscription-renew-cta"
          className={`gap-2 ${isExpired ? "bg-[#B42318] hover:bg-[#912018]" : "bg-[#1F4E3D] hover:bg-[#2E5D4E]"} text-white`}
        >
          <Crown size={14} /> {paying ? "Opening…" : (isExpired ? "Renew Subscription" : "Manage Plan")} <ArrowRight size={14} />
        </Button>
      </div>

      {/* Detail row */}
      {!compact && (
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mt-5">
          <DetailTile
            icon={<CalendarIcon size={14} />}
            label="Start Date"
            value={fmtDate(sub.start_date)}
            testid="subscription-start-date"
          />
          <DetailTile
            icon={<CalendarIcon size={14} />}
            label="Expiry Date"
            value={fmtDate(sub.expiry_date)}
            testid="subscription-expiry-date"
          />
          <DetailTile
            icon={<Clock size={14} />}
            label={isExpired ? "Expired" : "Days Remaining"}
            value={daysLeft == null ? "—" : (isExpired ? `${Math.abs(daysLeft)} ${dayLabel} ago` : `${daysDisplay} ${dayLabel}`)}
            testid="subscription-days-remaining"
            valueClass={isExpired ? "text-[#B42318]" : (sub.status === "expiring_soon" ? "text-[#92400E]" : "text-[#111827]")}
          />
        </div>
      )}

      {/* Expiry warning banner variant — only when actually expired */}
      {isExpired && !compact && (
        <div
          data-testid="subscription-expired-banner"
          className="mt-4 rounded-xl border border-[#FCA5A5] bg-[#FEF2F2] px-4 py-3 text-sm text-[#991B1B] flex items-start gap-2"
        >
          <AlertTriangle size={16} className="shrink-0 mt-0.5" />
          <div>
            <strong>Your subscription has expired.</strong> Renew now to restore
            your {isPremium ? "Premium" : "Featured"} benefits — priority ranking,
            larger galleries, {isPremium ? "WhatsApp CTAs, online booking " : ""}
            and enhanced visibility across Pune Spa Guide.
          </div>
        </div>
      )}

      {/* Payment summary dialog — opens before Razorpay so the owner sees
          the GST breakdown transparently. */}
      <PaymentSummaryDialog
        open={showSummary}
        onCancel={() => setShowSummary(false)}
        onConfirm={() => {
          openCheckout({
            plan: sub.plan,
            prefill: { name: user?.name, email: user?.email, contact: user?.phone },
          });
          setShowSummary(false);
        }}
        plan={sub.plan}
        pricing={summaryPricing}
        loading={paying}
      />
    </div>
  );
}

function DetailTile({ icon, label, value, testid, valueClass = "text-[#111827]" }) {
  return (
    <div className="rounded-xl bg-[#F5EFE6] border border-[#E7DDCF] px-4 py-3">
      <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-widest text-[#7A5A22]">
        {icon} {label}
      </div>
      <div className={`mt-1 font-heading text-lg ${valueClass}`} data-testid={testid}>{value}</div>
    </div>
  );
}
